import { useState } from 'react';
import { Button, Input, Popconfirm, Segmented, Space, Table, Tag, Tooltip, Typography, type TableColumnsType } from 'antd';
import { usePermissions } from '../../auth/store';
import { QueryError } from '../../lib/QueryState';
import { LoadMore } from '../../lib/useCursorPages';
import { Amounts } from '../agencies/agencyViews';
import { PortalPage } from './AgencyPortalPage';
import { usePortalSummary, usePortalUsers, type PortalUserRow } from './portalApi';
import { ReasonModal, useBlockActions } from './portalActions';

type Filter = 'all' | 'active' | 'blocked';

const SOURCE_LABEL: Record<PortalUserRow['source'], string> = { manual: 'Code', link: 'Link / QR', admin: 'Dude team' };
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/**
 * `/agency-portal/users`: the users who joined with this agency's code and
 * never applied as a listener. Activity is shown in totals only — no phone
 * numbers, chats or call content. The agency can block / unblock them.
 */
export function AgencyUsersPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const list = usePortalUsers({ status: filter === 'all' ? undefined : filter, q });
  // Permission AND the Dude team's switch for this agency (the API checks both).
  const controls = usePortalSummary().data?.agency.controls;
  const canBlock = usePermissions().can('agency_portal.users.block') && !!controls?.users;
  const { block, unblock } = useBlockActions();
  const [blocking, setBlocking] = useState<PortalUserRow | null>(null);

  const columns: TableColumnsType<PortalUserRow & { id: string }> = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <span>
          @{r.handle}
          {r.displayName ? <Typography.Text type="secondary"> · {r.displayName}</Typography.Text> : null}
          {r.blocked && (
            <Tooltip title={r.blockReason ?? undefined}>
              <Tag color="error" style={{ marginLeft: 6 }}>
                Blocked
              </Tag>
            </Tooltip>
          )}
          {r.deleted && <Tag style={{ marginLeft: 6 }}>Deleted</Tag>}
        </span>
      ),
    },
    { title: 'Joined', dataIndex: 'joinedAt', render: (v: string) => day(v) },
    { title: 'Via', dataIndex: 'source', render: (s: PortalUserRow['source']) => SOURCE_LABEL[s] ?? s },
    { title: 'Recharged', key: 'recharges', render: (_, r) => <Amounts rows={r.recharges} empty="—" /> },
    { title: 'Last recharge', dataIndex: 'lastRechargeAt', render: (v: string | null) => day(v) },
    { title: 'Calls', dataIndex: 'calls', align: 'right', render: (n: number) => n.toLocaleString('en-IN') },
    { title: 'Last call', dataIndex: 'lastCallAt', render: (v: string | null) => day(v) },
    ...(canBlock
      ? [
          {
            title: '',
            key: 'actions',
            render: (_: unknown, r: PortalUserRow) =>
              r.deleted ? null : r.blocked ? (
                <Popconfirm title={`Unblock @${r.handle}?`} onConfirm={() => unblock.mutate({ userId: r.userId })}>
                  <Button size="small" loading={unblock.isPending && unblock.variables?.userId === r.userId}>
                    Unblock
                  </Button>
                </Popconfirm>
              ) : (
                <Button size="small" danger onClick={() => setBlocking(r)}>
                  Block
                </Button>
              ),
          },
        ]
      : []),
  ];

  return (
    <PortalPage title="Users">
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <Segmented<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'blocked', label: 'Blocked' },
          ]}
        />
        <Input.Search allowClear placeholder="Search name or @handle" onSearch={setQ} style={{ width: 260 }} />
      </Space>
      {list.isError ? (
        <QueryError error={list.error} what="users" onRetry={list.refetch} />
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            columns={columns}
            dataSource={list.rows}
            pagination={false}
            scroll={{ x: 900 }}
            locale={{ emptyText: 'No users yet' }}
          />
          <LoadMore list={list} noun="users" />
        </>
      )}
      <ReasonModal
        open={!!blocking}
        title={`Block @${blocking?.handle ?? ''}?`}
        description="They are signed out and cannot use the app until unblocked. Write why — it is kept in the activity log."
        okText="Block"
        loading={block.isPending}
        onOk={(reason) => blocking && block.mutate({ userId: blocking.userId, reason }, { onSuccess: () => setBlocking(null) })}
        onCancel={() => setBlocking(null)}
      />
    </PortalPage>
  );
}
